import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Image,
    Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import type { Ledger } from '../../../../types/CreateProductType';
import type { ProjectType } from "../../../../types/ProjectType";

const { TextArea } = Input;

interface ProjectModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: ProjectType | null;
    isEditMode?: boolean;
    projects: ProjectType[];
    ledgers: Ledger[];
}

const ProjectModal: React.FC<ProjectModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    projects,
    ledgers,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [profileImageFileList, setProfileImageFileList] = useState<UploadFile[]>([]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState<string>('');

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddProject = async (values: any) => {
        try {
            const formData = new FormData();

            formData.append('project_name', values.project_name);
            formData.append('project_description', values.project_description);
            formData.append('project_ledger_id', values.project_ledger_id);

            if (imageFile) {
                formData.append('attachments', imageFile); // ✅ binary file
            }

            if (initialValues?.project_id) {
                formData.append('project_id', initialValues.project_id.toString());
            }
            const url = isEditMode
                ? 'edit-project'
                : 'add-project';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log(formData);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'project updated successfully!' : 'project created successfully!',
            );
            form.resetFields();
            setProfileImageFileList([]);
            setImageFile(null);
            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} project`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                project_name: initialValues.project_name,
                project_ledger_id: initialValues.project_ledger_id,

            });
            if (initialValues.project_image) {
                const imageUrl = `${initialValues.project_image}`;
                setProfileImageFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: imageUrl,
                    },
                ]);
                setImageFile(null); // prevent old file being reused
            }
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);


    const uploadButton = (
        <div
            style={{
                width: 220,
                height: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <PlusOutlined style={{ fontSize: 24 }} />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    //  Convert file to base64 for preview
    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview && file.originFileObj) {
            file.preview = await getBase64(file.originFileObj);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    return (
        <Modal
            title={isEditMode ? 'Edit project' : 'Add New project'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddProject}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="project_ledger_id" label="Select Client" rules={[{ required: true }]}>
                            <Select
                                placeholder="Select Client"
                                options={ledgers.map(l => ({ label: l.ledger_first_name, value: l.ledger_id }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="project_name"
                            className='px-8'
                            label={
                                <span>
                                    Project Name &nbsp;
                                    <span style={{ fontSize: '12px', color: '#888' }}>
                                        (Max 100 characters)
                                    </span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter the project Name' },
                                { max: 100, message: 'Maximum 100 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={100}
                                placeholder="e.g., project "

                            />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item label="Upload project Image" required>
                            <Upload
                                beforeUpload={() => false}
                                listType="picture-card"
                                maxCount={1}
                                fileList={profileImageFileList}
                                onPreview={handlePreview}
                                onChange={({ fileList }) => {
                                    setProfileImageFileList(fileList);

                                    const rawFile = fileList[0]?.originFileObj;
                                    if (rawFile instanceof File) {
                                        setImageFile(rawFile); // ✅ set real File here
                                    } else {
                                        setImageFile(null);
                                    }
                                }}
                            >
                                {profileImageFileList.length >= 1 ? null : uploadButton}
                            </Upload>
                            {/* Ant Design Image Preview */}
                            {previewImage && (
                                <Image
                                    wrapperStyle={{ display: 'none' }}
                                    preview={{
                                        visible: previewOpen,
                                        onVisibleChange: (visible) => setPreviewOpen(visible),
                                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                    }}
                                    src={previewImage}
                                />
                            )}
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="project_description"
                            className='px-8'
                            label={
                                <span>
                                    Description
                                </span>
                            }
                        >
                            <TextArea rows={4} />
                        </Form.Item>
                    </Col>
                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New project
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProjectModal;
